"use client";

import React, { useEffect, useRef, useState } from "react";
import { Loader2, Lock, Users } from "lucide-react";
import { cn } from "@/src/lib/utils";
import { WISHLIST_NAME_MAX_LENGTH, wishlistService } from "../services";
import type { TWishlistVisibility } from "../types";

export interface IWishlistNameValues {
  name: string;
  visibility: TWishlistVisibility;
  isDefault: boolean;
}

interface IWishlistNameFormProps {
  initialName?: string;
  /** Create shows "Who can see it" and "Make it my default"; rename does not. */
  withOptions?: boolean;
  submitLabel: string;
  /** Wait before focusing the name, e.g. until a slide-in ends. */
  focusDelayMs?: number;
  /** Resolves when done; a rejection keeps the form open for another try. */
  onSubmit: (values: IWishlistNameValues) => Promise<void>;
}

const VISIBILITY_OPTIONS = [
  {
    value: "shared" as const,
    label: "Shared",
    caption: "Anyone with link",
    Icon: Users,
  },
  {
    value: "private" as const,
    label: "Private",
    caption: "Only you",
    Icon: Lock,
  },
];

/** The app's create / rename sheet body. */
const WishlistNameForm: React.FC<IWishlistNameFormProps> = ({
  initialName = "",
  withOptions = false,
  submitLabel,
  focusDelayMs = 0,
  onSubmit,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // `preventScroll`: focusing must not scroll the sheet while it is still moving.
  useEffect(() => {
    const timer = window.setTimeout(
      () => inputRef.current?.focus({ preventScroll: true }),
      focusDelayMs,
    );
    return () => window.clearTimeout(timer);
  }, [focusDelayMs]);

  const [name, setName] = useState(initialName);
  const [visibility, setVisibility] = useState<TWishlistVisibility>("shared");
  const [isDefault, setIsDefault] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const validName = wishlistService.validName(name);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validName || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit({ name: validName, visibility, isDefault });
    } catch {
      // The caller has shown the error; the form stays as it was.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-5 px-6 pb-6 pt-3">
      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-ink-black">Name</span>
        <input
          ref={inputRef}
          value={name}
          // As the app's field: sentence case, and the keyboard's key submits.
          autoCapitalize="sentences"
          enterKeyHint="done"
          maxLength={WISHLIST_NAME_MAX_LENGTH}
          disabled={isSubmitting}
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. Cox’s Bazar trip"
          className="h-12 rounded-xl border border-[#C3C6D3] px-4 text-base text-ink-black outline-none transition-colors focus:border-neutral-900 disabled:bg-gray-50"
        />
        <span className="self-end text-xs text-ink-gray">
          {name.length}/{WISHLIST_NAME_MAX_LENGTH}
        </span>
      </label>

      {withOptions && (
        <>
          <div className="flex flex-col gap-3">
            <span className="text-base font-semibold text-ink-black">
              Who can see it
            </span>
            <div className="grid grid-cols-2 gap-3">
              {VISIBILITY_OPTIONS.map(({ value, label, caption, Icon }) => {
                const isSelected = visibility === value;
                return (
                  <button
                    key={value}
                    type="button"
                    disabled={isSubmitting}
                    aria-pressed={isSelected}
                    onClick={() => setVisibility(value)}
                    className={cn(
                      "flex flex-col items-start gap-1 rounded-xl p-4 text-left transition-colors",
                      isSelected
                        ? "border-[1.5px] border-neutral-900 text-ink-black"
                        : "border border-[#C3C6D3] text-ink-gray",
                    )}
                  >
                    <Icon size={20} />
                    <span className="text-sm font-semibold">{label}</span>
                    <span className="text-xs text-ink-gray">{caption}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <span className="flex flex-col">
              <span className="text-base font-semibold text-ink-black">
                Make it my default
              </span>
              <span className="text-sm text-ink-gray">
                Quick saves land here
              </span>
            </span>
            <button
              type="button"
              role="switch"
              aria-label="Make it my default"
              aria-checked={isDefault}
              disabled={isSubmitting}
              onClick={() => setIsDefault((value) => !value)}
              className={cn(
                "relative h-7 w-12 shrink-0 rounded-full transition-colors",
                isDefault ? "bg-neutral-900" : "bg-[#C3C6D3]",
              )}
            >
              <span
                className={cn(
                  "absolute left-0 top-1 h-5 w-5 rounded-full bg-white shadow transition-transform",
                  isDefault ? "translate-x-6" : "translate-x-1",
                )}
              />
            </button>
          </div>
        </>
      )}

      <button
        type="submit"
        disabled={!validName || isSubmitting}
        className="flex h-[52px] items-center justify-center rounded-xl bg-neutral-900 text-base font-semibold text-white transition-colors disabled:bg-[#C3C6D3]"
      >
        {isSubmitting ? (
          <Loader2 size={20} className="animate-spin" />
        ) : (
          submitLabel
        )}
      </button>
    </form>
  );
};

export default WishlistNameForm;
